import React, { useState, useEffect } from "react";

/**
 * AIGeneratedText Component - Animated text display with decryption effect
 * 
 * Shows a hacking/decryption style animation where random characters
 * are displayed before revealing the final text character by character.
 * 
 * @param {Object} props - Component properties
 * @param {string} props.text - The final text to display after animation
 * @param {boolean} props.isAnimating - Whether the animation should be active
 * @param {function} props.onComplete - Callback function when animation completes
 * @param {Object} props.style - Additional styles to apply to the text
 * @param {string} props.characters - Custom characters to use for random animation (default: A-Z, 0-9)
 * @param {number} props.speed - Animation speed in milliseconds (default: 50)
 * @param {number} props.increment - How fast each character is revealed (default: 1/3)
 * 
 * @example
 * <AIGeneratedText 
 *   text="MEPARFU-01" 
 *   isAnimating={true} 
 *   onComplete={() => console.log('Animation done!')}
 *   style={{ color: '#00ff00', fontSize: '16px' }}
 * />
 */
const AIGeneratedText = ({ 
  text = "", 
  isAnimating = false, 
  onComplete, 
  style = {}, 
  characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
  speed = 50,
  increment = 1/3
}) => {
  const [displayText, setDisplayText] = useState("");
  const [iteration, setIteration] = useState(0);
  
  useEffect(() => {
    // Reset iteration when text changes or animation starts
    if (isAnimating) {
      setIteration(0);
    }
  }, [text, isAnimating]);
  
  useEffect(() => {
    if (!isAnimating) {
      setDisplayText(text);
      return;
    }
    
    let animationInterval;
    
    const animate = () => {
      setDisplayText((current) =>
        text
          .split("")
          .map((letter, index) => {
            // If this character position has been revealed, show the real character
            if (index < iteration) {
              return text[index];
            }
            // Otherwise show a random character
            return characters[Math.floor(Math.random() * characters.length)];
          })
          .join("")
      );
      
      // If all characters have been revealed
      if (iteration >= text.length) {
        clearInterval(animationInterval);
        setDisplayText(text);
        onComplete && onComplete();
        return;
      }
      
      // Move to next character
      setIteration(prev => prev + increment);
    };
    
    animationInterval = setInterval(animate, speed);
    
    return () => clearInterval(animationInterval);
  }, [text, isAnimating, iteration, onComplete, characters, speed, increment]);
  
  return (
    <span 
      style={{ 
        fontFamily: 'monospace',
        letterSpacing: '0.05em',
        ...style 
      }}
    >
      {displayText}
    </span>
  );
};

export default AIGeneratedText;
